import React from 'react'
import { useApp } from '../context/AppContext'
import { NeonCity, VolcanicArena, AncientTemple, SpaceStation, Battlefield } from '../components/stages'

const stageComponents = {
  neonPier: NeonCity,
  reactorCore: VolcanicArena,
  skySpire: AncientTemple,
  ironRing: SpaceStation,
  battlefield: Battlefield,
}

const stageThemes = {
  neonPier: 'from-cyan-400 to-blue-500',
  reactorCore: 'from-orange-400 to-red-500',
  skySpire: 'from-pink-400 to-purple-500',
  ironRing: 'from-emerald-400 to-teal-500',
  battlefield: 'from-green-400 to-blue-500',
}

export function Stages() {
  const { state, dispatch, stagePresets } = useApp()

  const selectedStage = stagePresets[state.currentStageId]
  const SelectedComponent = stageComponents[state.currentStageId] || NeonCity

  const handleSelectStage = (stageId) => {
    dispatch({ type: 'SET_STAGE', payload: stageId })
  }

  return (
    <div className="space-y-8">
      {/* Main Preview Area */}
      <div className="relative w-full aspect-[2/1] rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black group">
        <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-105">
          <SelectedComponent />
        </div>

        {/* Overlay UI */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-6 md:p-10">
          <h2 className="text-2xl md:text-4xl font-bold uppercase tracking-wider drop-shadow-lg">
            {selectedStage.name}
          </h2>
          <p className="text-gray-300 mt-2 text-sm md:text-base max-w-xl">
            {selectedStage.description}
          </p>
          <div className="flex items-center gap-2 mt-4">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-sm font-medium text-gray-300 uppercase tracking-widest">
              Ready to fight
            </span>
          </div>
        </div>
      </div>

      {/* Stage Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {Object.values(stagePresets).map((stage) => {
          const isSelected = state.currentStageId === stage.id
          const StageComponent = stageComponents[stage.id]
          const theme = stageThemes[stage.id]

          return (
            <button
              key={stage.id}
              onClick={() => handleSelectStage(stage.id)}
              className={`
                relative group flex flex-col text-left rounded-xl overflow-hidden transition-all duration-300
                ${isSelected
                  ? 'ring-4 ring-cyan-500 scale-[1.02] shadow-[0_0_30px_rgba(6,182,212,0.4)] z-10'
                  : 'ring-1 ring-white/10 hover:ring-white/30 hover:scale-[1.02] opacity-80 hover:opacity-100'
                }
              `}
            >
              {/* Thumbnail Preview */}
              <div className="w-full aspect-video bg-black relative overflow-hidden">
                <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-110">
                  <StageComponent />
                </div>
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-300" />

                {/* Selected Indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Label */}
              <div
                className={`
                  p-3 font-bold text-sm uppercase tracking-wide transition-all duration-300
                  ${isSelected
                    ? `bg-gradient-to-r ${theme} text-black`
                    : 'bg-gray-900 text-gray-300 group-hover:text-white'
                  }
                `}
              >
                {stage.name}
              </div>
            </button>
          )
        })}
      </div>

      {/* Stage Info Card */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">
              Selected Arena
            </span>
            <h3 className="text-2xl font-bold mt-1">{selectedStage.name}</h3>
            <p className="text-gray-400 mt-2">{selectedStage.description}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase bg-gradient-to-r ${stageThemes[state.currentStageId]} text-black`}>
            {selectedStage.label}
          </span>
        </div>
      </div>
    </div>
  )
}
